import { type JSX } from 'react';

import { Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { type EscolaInfoResponse } from '@/features/benchmarking/schemas/benchmarkingInfo';

import { PdfDataTable } from '../components/PdfDataTable';
import { coverAssets } from '../lib/coverAssets';
import { type ImageSize, scaleToHeight, scaleToWidth } from '../lib/imageScaling';
import {
  PDF_BORDER_RADIUS,
  PDF_COLORS,
  PDF_FONT_SIZES,
  PDF_FONT_WEIGHTS,
  PDF_FONTS,
  PDF_SPACING,
} from '../lib/pdfTokens';

type CoverPageProps = Pick<EscolaInfoResponse, 'nomeEscola' | 'perfil' | 'cidade' | 'uf' | 'qtdAlunos' | 'qtdTurmas'>;

const female: ImageSize = scaleToHeight(coverAssets.characterFemale, 470);
const male: ImageSize = scaleToHeight(coverAssets.characterMale, 330);
const computer: ImageSize = scaleToWidth(coverAssets.computer, 234);
const papers: ImageSize = scaleToWidth(coverAssets.papers, 207);
const books: ImageSize = scaleToWidth(coverAssets.bookStack, 145);

const styles = StyleSheet.create({
  page: {
    backgroundColor: PDF_COLORS.white,
    fontFamily: PDF_FONTS.sans,
  },
  hero: {
    backgroundColor: PDF_COLORS.brand,
    height: 583,
    flexDirection: 'row',
    paddingTop: PDF_SPACING[10],
    paddingLeft: 35,
    marginTop: 35,
    marginHorizontal: 30,
    borderRadius: PDF_BORDER_RADIUS.lg,
  },
  heroText: {
    width: '60%',
  },
  title: {
    width: 327,
    fontSize: PDF_FONT_SIZES['3xl'],
    fontWeight: PDF_FONT_WEIGHTS.bold,
    color: PDF_COLORS.white,
    lineHeight: 1.2,
    marginBottom: PDF_SPACING[4],
  },
  subtitle: {
    width: 233,
    fontSize: PDF_FONT_SIZES.base,
    color: PDF_COLORS.white,
    opacity: 0.85,
    lineHeight: 1.5,
  },
  illustration: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  divider: {
    position: 'absolute',
    bottom: -2,
    left: -30,
    right: -30,
    height: 2,
    backgroundColor: PDF_COLORS.black,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metaTable: {
    width: 310,
  },
  footerRight: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    alignSelf: 'stretch',
    paddingLeft: PDF_SPACING[8],
  },
  dadosIlustrativos: {
    fontSize: 8,
    fontWeight: 300,
    color: PDF_COLORS.textSecondary,
    lineHeight: 1.35,
  },
  logo: {
    width: 120,
    height: 24,
  },
});

function clipped(size: ImageSize): ImageSize {
  return { width: size.width, height: size.height - 15 };
}

function Illustration(): JSX.Element {
  return (
    <View style={styles.illustration}>
      <View style={{ position: 'absolute', ...clipped(female), right: 35, bottom: 0, overflow: 'hidden' }}>
        <Image
          src={coverAssets.characterFemale.uri}
          style={{ position: 'absolute', ...female, top: 0, left: 0 }}
        />
      </View>
      <View style={{ position: 'absolute', ...clipped(male), right: female.width + 35, bottom: 0, overflow: 'hidden' }}>
        <Image
          src={coverAssets.characterMale.uri}
          style={{ position: 'absolute', ...male, top: 0, left: 0 }}
        />
      </View>
      <Image
        src={coverAssets.computer.uri}
        style={{ position: 'absolute', ...computer, right: -20, bottom: -45 }}
      />
      <Image
        src={coverAssets.papers.uri}
        style={{ position: 'absolute', ...papers, right: computer.width - 50, bottom: (papers.height + 10) * -1 }}
      />
      <Image
        src={coverAssets.bookStack.uri}
        style={{ position: 'absolute', ...books, left: -20, bottom: -46 }}
      />
    </View>
  );
}

export function CoverPage({ nomeEscola, perfil, cidade, uf, qtdAlunos, qtdTurmas }: CoverPageProps): JSX.Element {
  const localizacao = [cidade, uf].filter(Boolean).join(', ');
  const alunosETurmas = [
    qtdAlunos !== null ? `${qtdAlunos} alunos` : null,
    qtdTurmas !== null ? `${qtdTurmas} turmas` : null,
  ]
    .filter(Boolean)
    .join(' | ');

  return (
    <Page
      size='A4'
      style={styles.page}>
      <View style={styles.hero}>
        <View style={styles.heroText}>
          <Text style={styles.title}>{'Inteligência\nCompetitiva para\nEscolas'}</Text>
          <Text style={styles.subtitle}>Como dados pedagógicos e de mercado podem orientar ações estratégicas*</Text>
        </View>
        <View style={styles.divider} />
        <Illustration />
      </View>

      <View style={styles.footer}>
        <View style={styles.metaTable}>
          <PdfDataTable
            labelWidth={95}
            valueWidth={215}
            rows={[
              { label: 'Escola', value: nomeEscola },
              perfil ? { label: 'Perfil', value: perfil } : null,
              localizacao ? { label: 'Localização', value: localizacao } : null,
              alunosETurmas ? { label: 'Alunos e Turmas', value: alunosETurmas } : null,
            ].filter((r): r is { label: string; value: string } => r !== null)}
          />
        </View>
        <View style={styles.footerRight}>
          <Text style={styles.dadosIlustrativos}>*Dados ilustrativos</Text>
          <Image
            src={coverAssets.logo.uri}
            style={styles.logo}
          />
        </View>
      </View>
    </Page>
  );
}
